// Old flat shape (pre-Markdown parser)
export type ArticleSectionLegacy = { heading: string; body: string[] }
// Typed shape emitted by ap_parse_article_body() (PHP) and
// parseMarkdownToSections() (below, its TS mirror for the live editor preview)
export type ArticleSectionTyped =
  | { type: "heading"; text: string }
  | { type: "subheading"; text: string }
  | { type: "paragraph"; text: string }
  | { type: "bullet_list"; items: string[] }
  | { type: "numbered_list"; items: string[] }
  | { type: "callout"; text: string }
  | { type: "image"; src: string; caption?: string }
export type ArticleSection = ArticleSectionLegacy | ArticleSectionTyped

/** Convert inline **bold** / *italic* / _italic_ Markdown to safe HTML spans. */
export function inlineMarkdown(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
  return escaped
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
}

/** Only allow http(s) image sources — never data:/javascript: from the DB. */
function safeImageSrc(src: string): string | null {
  return /^https?:\/\//i.test(src.trim()) ? src.trim() : null
}

/**
 * TypeScript mirror of PHP `ap_parse_article_body()` in
 * public/portal/playquest-telemetry-lib.php. Kept deliberately small and in
 * lock-step with that function so the /ontdek inline-editor preview renders
 * exactly what the server will produce on save. The published page still
 * uses the server-parsed `sections`; this is only for the live preview.
 */
export function parseMarkdownToSections(raw: string): ArticleSectionTyped[] {
  const lines = raw.split(/\r?\n/)
  const sections: ArticleSectionTyped[] = []
  let pending: string[] = []
  let pType: "paragraph" | "bullet_list" | "numbered_list" | null = null

  const flush = () => {
    if (!pending.length || pType === null) return
    if (pType === "paragraph") {
      const text = pending.join(" ").trim()
      if (text) sections.push({ type: "paragraph", text })
    } else {
      sections.push({ type: pType, items: pending })
    }
    pending = []
    pType = null
  }

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, "")

    if (line.trim() === "") {
      if (pType === "paragraph") flush()
      continue
    }

    let m: RegExpMatchArray | null
    if ((m = line.match(/^##\s+(.+)/))) { flush(); sections.push({ type: "heading", text: m[1].trim() }); continue }
    if ((m = line.match(/^###\s+(.+)/))) { flush(); sections.push({ type: "subheading", text: m[1].trim() }); continue }
    if ((m = line.match(/^>\s*(.+)/))) { flush(); sections.push({ type: "callout", text: m[1].trim() }); continue }
    if ((m = line.match(/^!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)\s*$/))) {
      flush(); sections.push({ type: "image", src: m[2].trim(), caption: m[1].trim() }); continue
    }
    if ((m = line.match(/^[-*]\s+(.+)/))) {
      if (pType !== "bullet_list") { flush(); pType = "bullet_list" }
      pending.push(m[1].trim()); continue
    }
    if ((m = line.match(/^\d+\.\s+(.+)/))) {
      if (pType !== "numbered_list") { flush(); pType = "numbered_list" }
      pending.push(m[1].trim()); continue
    }

    if (pType !== "paragraph") { flush(); pType = "paragraph" }
    pending.push(line)
  }

  flush()
  return sections
}

const html = (s: string) => ({ __html: inlineMarkdown(s) })

/** Renders an article body — shared by the published page and the editor preview. */
export default function ArticleBody({ sections }: { sections: ArticleSection[] }) {
  return (
    <>
      {(sections ?? []).map((section, i) => {
        if ("type" in section) {
          const s = section
          switch (s.type) {
            case "heading":
              return <h2 key={i} className="article-section-heading" dangerouslySetInnerHTML={html(s.text)} />
            case "subheading":
              return <h3 key={i} className="article-section-subheading" dangerouslySetInnerHTML={html(s.text)} />
            case "paragraph":
              return <p key={i} className="article-section-paragraph" dangerouslySetInnerHTML={html(s.text)} />
            case "bullet_list":
              return (
                <ul key={i} className="article-section-list">
                  {s.items.map((item, j) => (
                    <li key={j} dangerouslySetInnerHTML={html(item)} />
                  ))}
                </ul>
              )
            case "numbered_list":
              return (
                <ol key={i} className="article-section-list article-section-list--numbered">
                  {s.items.map((item, j) => (
                    <li key={j} dangerouslySetInnerHTML={html(item)} />
                  ))}
                </ol>
              )
            case "callout":
              return (
                <div key={i} className="article-callout">
                  <span dangerouslySetInnerHTML={html(s.text)} />
                </div>
              )
            case "image": {
              const src = safeImageSrc(s.src)
              if (!src) return null
              return (
                <figure key={i} className="article-figure">
                  <img src={src} alt={s.caption || ""} loading="lazy" />
                  {s.caption ? <figcaption dangerouslySetInnerHTML={html(s.caption)} /> : null}
                </figure>
              )
            }
            default:
              return null
          }
        }

        // Legacy flat section (heading + body[])
        const ls = section
        return (
          <section key={i} className="article-section">
            {ls.heading && <h2>{ls.heading}</h2>}
            {ls.body.map((para, j) => (
              <p key={j}>{para}</p>
            ))}
          </section>
        )
      })}
    </>
  )
}
